import type { EnterpriseContract_EnterpriseContract } from '__generated__/graphql-types-do-not-use';
import gql from 'graphql-tag';
import { filter, values } from 'lodash';
import moment from 'moment';

import keysToConstants from 'js/lib/keysToConstants';
import { formatDateTimeDisplay } from 'js/utils/DateTimeUtils';

import type {
  LinkProgramsToContractRequest,
  LinkProgramsToContractResponse,
} from '@coursera/grpc-types-enterpriseuser/coursera/proto/enterpriseuser/rosters/v1/rosters_api';

import { enterpriseContractType } from 'bundles/enterprise-admin-constants/EnterpriseContractConstants';
import type { enterpriseContractUtilizationType } from 'bundles/enterprise-admin-constants/EnterpriseContractConstants';
import type {
  ImportFromContractOption,
  ProgramImportOptionMap,
} from 'bundles/enterprise-admin-contracts/components/ImportFromContractDrawer';
import type {
  ProgramImportOption,
  SelectedUserCourseEnrollment,
} from 'bundles/enterprise-admin-contracts/components/ProgramImportSelector';
import type { ContractPurpose, ContractTag } from 'bundles/enterprise-admin-contracts/types';
import type { AccountGroup, DealType } from 'bundles/enterprise-admin-contracts/utils/SalesforceCaseUtils';
import type { NaptimeError } from 'bundles/naptimejs';
import EnterpriseContractProvisioningActions from 'bundles/naptimejs/resources/EnterpriseContractProvisioningActions.v1';
import EnterpriseContractPoliciesV1 from 'bundles/naptimejs/resources/enterpriseContractPolicies.v1';
import type EnterpriseProgram from 'bundles/naptimejs/resources/enterprisePrograms.v1';

import _t from 'i18n!nls/enterprise-admin-contracts';

export const CONTRACT_STATE_TYPES = keysToConstants(['CREATED', 'ACTIVE', 'PAUSED', 'TERMINATED', 'EXPIRED']);

export const RESTORABLE_CONTRACT_STATES = ['TERMINATED'];

const { STANDARD } = enterpriseContractType;

type $Values<O> = O[keyof O];
export type ContractType = $Values<typeof enterpriseContractUtilizationType>;

/**
 * We use ?number on the input[type="text"] elements so placeholder text can visible.
 *
 * If we use input[type="number"] then we re-introduce the bug where scrolling can trigger
 * scrolling values for input fields which could cause input mistakes from admins.
 *
 * There are many conditional parameters because the data type is used in many places, as many shapes.
 */
export type ContractSettings = {
  salesforceCaseNumber?: string;
  name: string;
  limitType: ContractType;
  licenseLimit?: number;
  enrollmentLimit?: number;
  coursesPerUserLimit?: number;
  durationInMonths?: number;
  tag?: ContractTag;
  salesforceBillable?: boolean;
  purpose?: ContractPurpose;
  licenseRotationTcEnabled?: boolean;
  accountGroup?: AccountGroup;
  dealType?: DealType;
  customLicenseRotation?: string;
};

export const EnterpriseContractBillingTypes = {
  INVOICE: 'INVOICE',
  NONE: 'NONE',
} as const;

export type BillingType = keyof typeof EnterpriseContractBillingTypes;

type RenewalContractSettings = ContractSettings & {
  originalContractId: string;
  importOption: ImportFromContractOption;
  renewalContractActivationDate?: number;
  isReal?: boolean;
};

export type EnterpriseContract = { __typename?: 'EnterpriseContract_EnterpriseContract' } & Pick<
  EnterpriseContract_EnterpriseContract,
  | 'id'
  | 'name'
  | 'startsAt'
  | 'createdAt'
  | 'hardPausedAt'
  | 'durationInMonths'
  | 'contractState'
  | 'endsAt'
  | 'endedAt'
  | 'contractType'
  | 'billingType'
  | 'isReal'
  | 'limitType'
  | 'purpose'
  | 'licenseRecycleLimitPercent'
>;

type ContractProgramMigrationConfiguration = {
  enrollmentMigrationConfiguration: string;
  invitationMigrationConfiguration: string;
  membershipMigrationConfiguration: string;
  enrollmentCutoffDate?: number;
  programId: string;
  refundCourseEnrollment: boolean;
  selectedUserCourseEnrollments: Array<SelectedUserCourseEnrollment>;
};

type ContractLimits = {
  userLimit?: number;
  coursesPerUserLimit?: number;
  enrollmentCapLimit?: number;
};

export const getCreateUpsellPolicyMutation = (oldContractId: string, newContractId: string) => {
  const data = {
    contractId: oldContractId,
    policyType: 'CONTRACT_FULLY_UTILIZED',
    policyMetaData: {
      typeName: 'ContractFullyUtilizedPolicyMetadata',
      definition: {
        fallbackContractId: newContractId,
      },
    },
  };
  return EnterpriseContractPoliciesV1.create(data);
};

export const getProgramImportOptionsFromMap = (
  programImportOptionsMap?: ProgramImportOptionMap
): ProgramImportOption[] => (programImportOptionsMap ? filter(values(programImportOptionsMap), (o) => o.selected) : []);

/**
 * Makes sure skipOldRosterCleanup is false for every program when archiveContract = true
 * This is required to pass validation on the BE
 * @param archiveContract
 * @param programImportOptionsMap
 */
export const validateArchiveContractAndGetNewOptionsMap = (
  archiveContract: boolean,
  programImportOptionsMap: ProgramImportOptionMap
): ProgramImportOptionMap => {
  if (!archiveContract) {
    return programImportOptionsMap;
  }
  const newProgramImportOptionsMap: ProgramImportOptionMap = {};
  Object.entries(programImportOptionsMap).forEach(([key, option]) => {
    newProgramImportOptionsMap[key] = {
      ...option,
      skipOldRosterCleanup: false,
    };
  });
  return newProgramImportOptionsMap;
};

export const getProgramMigrationConfigurations = (programImportOptions: ProgramImportOption[]) =>
  programImportOptions.map((o) => {
    const obj = {
      enrollmentMigrationConfiguration: o.enrollments.toUpperCase(),
      invitationMigrationConfiguration: o.invitees.toUpperCase(),
      membershipMigrationConfiguration: o.members.toUpperCase(),
      programId: o.id,
      refundCourseEnrollment: false,
      skipOldRosterCleanup: o.skipOldRosterCleanup,
      selectedUserCourseEnrollments: o.selectedUserCourseEnrollments,
    } as ContractProgramMigrationConfiguration;

    if (o.cutOffDate) {
      obj.enrollmentCutoffDate = moment(o.cutOffDate).valueOf();
    }

    return obj;
  });

export const getRenewContractMutation = (
  thirdPartyOrganizationId: string,
  contractSettings: RenewalContractSettings,
  billingType: string = EnterpriseContractBillingTypes.INVOICE
) => {
  const contractLimits: ContractLimits = {};
  const {
    limitType,
    licenseLimit,
    enrollmentLimit,
    name,
    durationInMonths,
    salesforceCaseNumber,
    importOption,
    originalContractId,
    tag,
    renewalContractActivationDate,
    isReal,
  } = contractSettings;

  contractLimits.userLimit = licenseLimit;
  contractLimits.enrollmentCapLimit = enrollmentLimit;
  const now = new Date().getTime();
  const data = {
    salesforceCaseNumber,
    name: name.trim(),
    limitType,
    thirdPartyOrganizationId,
    billingType: isReal ? billingType : EnterpriseContractBillingTypes.NONE,
    contractLimits,
    contractType: STANDARD,
    durationInMonths,
    planId: Math.floor(now / 1000),
    originalContractId,
    archiveContract: importOption.archiveOldContract,
    sendMembershipDeletionNotification: importOption.sendDeletionEmail,
    tag,
    programMigrationConfigurations: getProgramMigrationConfigurations(importOption.programImportOptions),
    renewalContractActivationDate,
    isReal,
  };

  return EnterpriseContractProvisioningActions.action('createRenewalContract', data);
};

export const appendResponseError = (errorText: string, errorObjectResponse?: NaptimeError | string) => {
  let appendedErrorText = errorText;
  const message =
    typeof errorObjectResponse === 'string' ? errorObjectResponse : errorObjectResponse?.responseJSON?.message;
  const code =
    typeof errorObjectResponse === 'string' ? errorObjectResponse : errorObjectResponse?.responseJSON?.errorCode;
  if (message) appendedErrorText += _t(' Error Message: #{message}.', { message });
  if (code) appendedErrorText += _t(' Error Code: #{code}.', { code });
  return appendedErrorText;
};

export const extractContractId = (val: string): string => {
  // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  return val.split('~')[0];
};

export const initProgramImportOptionsMap = (programs: EnterpriseProgram[]) => {
  return programs.reduce(
    (acc, p) => ({
      ...acc,
      [p.id]: {
        id: p.id,
        selected: false,
        invitees: 'all',
        members: 'all',
        enrollments: 'all',
        cutOffDate: null,
        skipOldRosterCleanup: true,
        selectedUserCourseEnrollments: [],
      },
    }),
    {}
  );
};

export const wasFreeTrialContract = (contract: { terms: Array<{ termTag: string }> }) =>
  // @ts-expect-error TS2532 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
  contract.terms.length > 1 && contract.terms[contract.terms.length - 1].termTag === 'C4T_FREE_TRIAL';

export const linkProgramsToContract = (body: LinkProgramsToContractRequest): Promise<LinkProgramsToContractResponse> =>
  fetch('/api/grpc/enterpriseuser/rosters/v1/RostersAPI/LinkProgramsToContract', {
    method: 'POST',
    body: JSON.stringify(body),
    headers: {
      'Content-Type': 'application/json',
    },
  }).then((response) => (response.ok ? response.json() : response.json().then((json) => Promise.reject(json))));

export const programsQuery = gql`
  query ProgramsQuery($programIds: [String!]!) {
    EnterpriseProgramsV1Resource {
      multiGet(ids: $programIds, limit: 999) {
        elements {
          id
          metadata {
            name
          }
        }
      }
    }
  }
`;

export const combineDateAndHour = ({ date, hour = 0 }: { date: string; hour?: number }) =>
  `${date} ${String(hour).padStart(2, '0')}:00`;

const timezoneUsedByCourseraLegalTeam = 'America/Los_Angeles';
export const formatContractTimestamp = (timestamp: number | string, format: string) =>
  formatDateTimeDisplay(timestamp, format, timezoneUsedByCourseraLegalTeam);
